import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../index';

import { toggleEditMode, updateWidgetProperty, splitPageBySelection } from 'actions/editor';
import { Property } from '../../components/properties';
import { PAGE, SECTION_COLUMN } from 'constants/widgetTypes';
import { getMediaInfo } from 'actions/pageHealth';

import { getWidgetType } from 'components/Editor/helper/widget';

import { useChildrenWidgetSelected, useAllowCreateLiveElement, useSelectedWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { Background } from 'components/Editor/components/properties/background';
import { LiveElementCreationProperty } from 'components/Editor/components/liveElements';
import StoryboardContext from 'components/Storyboard/context';
import { BackgroundView } from '../background';
import { Container } from '../core/container';

import { getBoxShadow } from 'services/cssHelper.service';

import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';
import { PAGE_CONTAIN_STORYBOARDS, STORYBOARD_TYPES } from 'constants/storyboardType';

import styles from './container.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const ContainerWidgetViewComponent = function ({ className, style, widgetProps, widgetRef, children }) {

  const dispatch = useDispatch();
  const ref = widgetRef;
  const prevBottomRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const selectedWidgets = useSelectedWidgetSelector(editor);

  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);

  const background = _.get(widgetProps, 'properties.desktop.background');
  const border = _.get(widgetProps, `properties.desktop.border`, {});
  const radius = border?.radius || {};


  const borderWidth = _.get(border, 'width', 0);

  const borderStyles = {
    borderTopLeftRadius: `${Math.max((radius.topLeft || 0) - borderWidth, 0)}px`,
    borderTopRightRadius: `${Math.max((radius.topRight || 0) - borderWidth, 0)}px`,
    borderBottomLeftRadius: `${Math.max((radius.bottomLeft || 0) - borderWidth, 0)}px`,
    borderBottomRightRadius: `${Math.max((radius.bottomRight || 0) - borderWidth, 0)}px`,
  };

  var ContainerWidgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, storyboardContext?.type),
   '--partial-selection-offset': `-${parseInt(borderWidth, 10) + 1}px`
  }

  const padding = Number(_.get(widgetProps, `properties.${breakpoint}.padding`, 0));

  const getBottomBoundary = (container) => {  
    const innerTop = container.getBoundingClientRect().top;
    const containerChildren = container.querySelectorAll('[data-widget-id]');

    let maxBottom = 0;
    containerChildren.forEach(child => {
      const bottom = child.getBoundingClientRect().bottom;
      maxBottom = Math.max(maxBottom, Math.ceil(bottom - innerTop));
    });
    return maxBottom;
  };

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const minHeight = getBottomBoundary(container) + padding;
    const currentContainerHeight = container.getBoundingClientRect().height;
    const currentHeight = _.get(widgetProps, `properties.${breakpoint}.layout.height`);

    const prevBottom = prevBottomRef.current;
    const isResized = !prevBottom || minHeight > prevBottom;
    prevBottomRef.current = minHeight;
    const isHeightChanged = isResized && minHeight > 0 && minHeight > currentContainerHeight;

    container.style.minHeight = minHeight > 0 ? `${minHeight}px` : '';

    if (isHeightChanged) {
      container.style.height = `${minHeight}px`;

      currentHeight !== minHeight && dispatch(
        updateWidgetProperty(editor, widgetProps.uid, breakpoint, {
          layout: {
            ...layout,
            height: `${minHeight}px`
          }
        })
      );
    }
  }, [childrenSelection, selectedWidgets, breakpoint, children]);

  return (
    <>
      <Container widgetProps={widgetProps} widgetRef={widgetRef}>
        <div
          id={id}
          ref={ref}
          className={cx(className, styles.container, 'widget', {
            [styles.partialSelection]: childrenSelection.selected,
            [styles.mobile]: breakpoint === 'mobile'
          })}
          style={ContainerWidgetStyle}
          data-widget-id={id}
          data-widget-type={type}
          data-selectable={!childrenSelection.selected}
          data-container={true}
        >
          <BackgroundView background={background} borderStyles={borderStyles} />
          <div className={styles.inner}>
            {children}
          </div>
        </div>
      </Container>
    </>
  );
};

const containerWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const ContainerWidgetView = React.memo(ContainerWidgetViewComponent, containerWidgetEquals);

const ContainerWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  useEffect(() => {
    setView(null);
  }, [id])

  const handleLiveElement = () => {
    setView('liveElements')
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [id]));
  };

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='border' onClick={() => changeView('border')}>
        <i className={cx("cleverstory-corner")} />
      </Property.Action>
      {makeFrameShow &&
        <>
          <Property.Divider />
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
        </>
      }
      <Property.Divider />
      {allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'background' &&
        <Background.Dialog
          id={id}
          value={background}
          showShadow={true}
          widgetProps={props}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {view === 'brandMedia' &&
        <BrandComponentProperty type={'CONTAINER'} widgets={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'border' &&
        <BorderPropertyDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const ContainerWidgetMobileAction = (props) => {
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');

  const changeView = (view) => {
    setView(view);
  }

  const closePopup = () => {
    setView(null);
  }

  return (
    <>
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'brandMedia' &&
        <BrandComponentProperty type={'CONTAINER'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const BorderPropertyDialog = ({ id, editor, widget, onCancel }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});
  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.sectionLayoutDialog}
      width={246}
      target={`#${id}`}
      title={'Border'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.formStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const ContainerWidget = React.forwardRef((props, ref) => {
  return <ContainerWidgetView {...props} ref={ref} />
});

ContainerWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <ContainerWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <ContainerWidgetMobileAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

ContainerWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  var layout = _.get(widget, 'properties.desktop.layout');
  const frame = new Frame(layout);

  if (!layout.width) {
    frame.remove('width');
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background,
        layout: frame.get()
      }
    }
  }
}

ContainerWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size: await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

ContainerWidget.moveableConfig = (editorSession, widget, breakpoint) => {

  return {
    draggable: true,
    resizable: true,
    rotatable: false,
    cloneable: true,
    deletable: true,
    renderDirections: ["s", "se", "sw", "e", "w", "n", "ne", "nw"],
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');

      var y = parseInt(values[1]);
      if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && storyboardType !== STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2) {
        y = Math.max(0, y);
      }

      const newTranslate = `${values[0]}, ${y}px`;

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    },
    onReparent: (widget, parent) => {
      const type = getWidgetType(editorSession, parent)
      if (type === PAGE || type === SECTION_COLUMN) return true;
      return false;
    }
  }
}

function getStyles(widgetProps, breakpoint, storyboardType) {

  var widgetStyles = {}
  const border = _.get(widgetProps, `properties.desktop.border`);
  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    widgetStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    widgetStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    widgetStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    widgetStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(border, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  widgetStyles['borderColor'] = color;

  const borderWidthString = _.get(border, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  widgetStyles['borderWidth'] = borderWidget;


  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
    }
  }

  //BoxShadow
  const shadow = _.get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

export { ContainerWidget };